import { startOfDay } from 'date-fns';
import * as yup from 'yup';
import type { FormDefinition } from '@gov-design-system-ce/react-form-core';
import {
	getFormDefinition,
	useGovForm,
} from '@gov-design-system-ce/react-form-core';
import type { DatovkaApiType } from '@gov-nx/api';
import { useAuthorizationConsentYupSchema } from '@gov-nx/core/hooks';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import {
	dataBoxParams,
	today,
	toStringDate,
	useDataBoxShape,
	yearsAgo,
	resetHours,
	useDateShape,
	monthsAgo,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { DataRequest, FormData } from './service.types';

export interface FormInstanceProps {
	code: ServiceCode;
	dataBoxTypes: DatovkaApiType['typSchranky'][];
	requiresConsent: boolean;
}

export function FormInstance({
	code,
	dataBoxTypes,
	requiresConsent,
}: FormInstanceProps): FormDefinition<FormData> {
	const YEARS_AGO_MAX = 2;
	const MONTHS_AGO_DEFAULT = 1;
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const { getDatePickerShape } = useDateShape();
	const tsn = getLocalizeCurried(code);
	const consentSchema = useAuthorizationConsentYupSchema({
		isConsentRequired: requiresConsent,
	});

	const { getDataBoxShape, getDataBoxDefaultValues } = useDataBoxShape({
		types: dataBoxTypes,
	});

	const formSchema = yup
		.object({
			...getDataBoxShape({ isRequired: true }),
			datumOd: getDatePickerShape({
				isInvalidMessage: tsn('form.validations.zadejte-datum-od'),
			})
				.min(
					resetHours(yearsAgo(YEARS_AGO_MAX)),
					tsn('form.validations.minimum-datum-od')
				)
				.max(today(), tsn('form.validations.maximum-datum-od'))
				.required(tsn('form.validations.zadejte-datum-od')),
			datumDo: getDatePickerShape({
				isInvalidMessage: tsn('form.validations.zadejte-datum-do'),
			})
				.required(tsn('form.validations.zadejte-datum-do'))
				.max(today(), tsn('form.validations.maximum-datum-do'))
				.min(yup.ref('datumOd'), tsn('form.validations.starsi-nez-datum-od')),
		})
		.shape({ __souhlas: consentSchema })
		.required();

	const formMethods = useGovForm<FormData>({
		formSchema,
		defaultValues: {
			...getDataBoxDefaultValues(),
			datumOd: monthsAgo(MONTHS_AGO_DEFAULT),
			datumDo: startOfDay(today()),
		},
	});

	return getFormDefinition({ formMethods, formSchema });
}

export const prepareSubmitData = (formData: FormData): DataRequest => ({
	params: dataBoxParams(formData),
	body: {
		typVypisu: 'UDAJE_ROB_VYUZITI',
		data: {
			datumOd: toStringDate(formData.datumOd),
			datumDo: toStringDate(formData.datumDo),
		},
	},
});
